import type { JSX } from 'react';
import { FormSection } from '@ksyos/design-system';
import type { BariPostNurseSpecialistFollowUpDataV1 } from '@common/validation/customControls/bariPost/bariPostNurseSpecialistFollowUp/v1/bariPostNurseSpecialistFollowUpDataV1';
import { CheckboxGroupOld } from '@frontend/components/fields/CheckBoxGroup';
import {
    useProcessActivityContext,
    useProcessActivityEditContext,
} from '@frontend/utils/hooks/useProcessActivityContext';
import {
    BooleanRadioGroupFormGroup,
    NumberInputColumnRowFormGroup,
    TextAreaFormGroup,
} from '../../processActivityFormGroupFields';
import { BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit } from './BariPostNurseSpecialistFollowUpComorbiditeitenStatusEdit';
import { BariPostNurseSpecialistFollowUpComorbiditeitenAdditionalFieldsV2 } from './v2/BariPostNurseSpecialistFollowUpComorbiditeitenAdditionalFieldsV2';

export function BariPostNurseSpecialistFollowUpComorbiditeitenEdit(): JSX.Element {
    const { customControlVersion } = useProcessActivityContext();
    const { data, errors, onChange } = useProcessActivityEditContext<BariPostNurseSpecialistFollowUpDataV1>();

    return (
        <FormSection title="Comorbiditeiten">
            <BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit
                mainFieldKey="hypertensie"
                mainLabel="Hypertensie"
                toelichtingFieldKey="hypertensie_medicamenteuze_veranderingen_toelichting"
                veranderingenFieldKey="hypertensie_medicamenteuze_veranderingen"
                veranderingenType="medicamenteuze"
                version={customControlVersion}
            />
            <BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit
                mainFieldKey="diabetes_mellitus"
                mainLabel="Diabetes mellitus"
                toelichtingFieldKey="diabetes_mellitus_medicamenteuze_veranderingen_toelichting"
                veranderingenFieldKey="diabetes_mellitus_medicamenteuze_veranderingen"
                veranderingenType="medicamenteuze"
                version={customControlVersion}
            />
            <NumberInputColumnRowFormGroup
                appendedLabel="mmol/mol"
                fieldKey="hba1c"
                isRequired={
                    customControlVersion === 1 ||
                    (Boolean(data.diabetes_mellitus) &&
                        customControlVersion === 2 &&
                        data.diabetes_mellitus !== 'Niet van toepassing')
                }
                label="HbA1c"
                maximumFractionDigits={2}
            />
            <BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit
                mainFieldKey="dyslipidemie"
                mainLabel="Dyslipidemie"
                toelichtingFieldKey="dyslipidemie_medicamenteuze_veranderingen_toelichting"
                veranderingenFieldKey="dyslipidemie_medicamenteuze_veranderingen"
                veranderingenType="medicamenteuze"
                version={customControlVersion}
            />
            <BooleanRadioGroupFormGroup
                fieldKey="reflux_klachten"
                isRequired={true}
                label="Klachten van reflux"
                onChange={handleChangeRefluxKlachten}
                type="radio"
            />
            {data.reflux_klachten && (
                <TextAreaFormGroup
                    fieldKey="reflux_klachten_toelichting"
                    isRequired={true}
                    label="Toelichting reflux"
                />
            )}
            <BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit
                mainFieldKey="gastroesophageal_reflux_disease"
                mainLabel="GERD"
                toelichtingFieldKey="gastroesophageal_reflux_disease_medicamenteuze_veranderingen_toelichting"
                veranderingenFieldKey="gastroesophageal_reflux_disease_medicamenteuze_veranderingen"
                veranderingenType="medicamenteuze"
                version={customControlVersion}
            />
            <BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit
                mainFieldKey="osas"
                mainLabel="OSAS"
                toelichtingFieldKey="osas_therapeutische_veranderingen_toelichting"
                veranderingenFieldKey="osas_therapeutische_veranderingen"
                veranderingenType="therapeutische"
                version={customControlVersion}
            />
            <BariPostNurseSpecialistFollowUpComorbiditeitStatusEdit
                mainFieldKey="gewrichtsklachten"
                mainLabel="Gewrichtsklachten"
                toelichtingFieldKey="gewrichtsklachten_therapeutische_veranderingen_toelichting"
                veranderingenFieldKey="gewrichtsklachten_therapeutische_veranderingen"
                veranderingenType="therapeutische"
                version={customControlVersion}
            />
            {customControlVersion === 2 && <BariPostNurseSpecialistFollowUpComorbiditeitenAdditionalFieldsV2 />}
            <BooleanRadioGroupFormGroup
                fieldKey="dumpingklachten"
                isRequired={true}
                label="Dumpingklachten"
                onChange={handleChangeDumpingklachten}
                type="radio"
            />
            {/* TODO: CS-19526 use processActivityFormGroupFields */}
            {data.dumpingklachten && (
                <>
                    <CheckboxGroupOld
                        checkboxOptions={[
                            {
                                checked: data.dumpingklachten_options.dumpingklachten_vroege_dumping ?? false,
                                label: 'Vroege dumping',
                                onChange: async (value) =>
                                    handleChangeDumpingklachtenOptions('dumpingklachten_vroege_dumping', value),
                            },
                            {
                                checked: data.dumpingklachten_options.dumpingklachten_late_dumping ?? false,
                                label: 'Late dumping',
                                onChange: async (value) =>
                                    handleChangeDumpingklachtenOptions('dumpingklachten_late_dumping', value),
                            },
                        ]}
                        error={errors.get('dumpingklachten_options')}
                        hasErrors={errors.has('dumpingklachten_options')}
                        isRequired={true}
                        label="Type dumpingklachten"
                        tooltipText="Vroege dumping: Vanaf 10 minuten tot een uur na de maaltijd. Een aanval kan tot een uur duren. Verschijnselen zijn o.a. vermoeidheid, veelvuldig gapen en een licht gevoel in het hoofd. Late dumping: 1 tot 3 uur na de maaltijd. Late dumpingklachten is een gevolg van hypoglykemie. Verschijnselen zijn o.a. zweten, trillen, slapte, gestoorde concentratie, verwardheid en een hongergevoel."
                    />
                    <TextAreaFormGroup
                        fieldKey="dumpingklachten_toelichting"
                        isRequired={true}
                        label="Toelichting dumpingklachten"
                    />
                </>
            )}
        </FormSection>
    );

    async function handleChangeRefluxKlachten(value: boolean | null): Promise<void> {
        return onChange(
            {
                reflux_klachten: value,
                reflux_klachten_toelichting: null,
            },
            'reflux_klachten'
        );
    }

    async function handleChangeDumpingklachten(value: boolean | null): Promise<void> {
        return onChange(
            {
                dumpingklachten: value,
                dumpingklachten_options: {
                    dumpingklachten_late_dumping: null,
                    dumpingklachten_vroege_dumping: null,
                },
                dumpingklachten_toelichting: null,
            },
            'dumpingklachten'
        );
    }

    async function handleChangeDumpingklachtenOptions(fieldKey: string, value: boolean): Promise<void> {
        onChange(
            {
                dumpingklachten_options: {
                    ...data.dumpingklachten_options,
                    [fieldKey]: value,
                },
            },
            'dumpingklachten_options'
        );
    }
}
